'use client';

import { useState, useEffect } from 'react';
import ProgramCard from './ProgramCard';
import FilterSidebar, { FilterState } from './FilterSidebar';
import SkillAssessmentModal from './SkillAssessmentModal';
import CareerPathCard from './CareerPathCard';
import Icon from '@/components/ui/AppIcon';

interface Program {
  id: string;
  title: string;
  description: string;
  image: string;
  alt: string;
  duration: string;
  level: string;
  skillsCount: number;
  projectsCount: number;
  studentsEnrolled: number;
  rating: number;
  category: string;
  price: string;
  featured: boolean;
  url: string;
  active: boolean;
}

interface CareerPath {
  title: string;
  description: string;
  icon: string;
  programs: number;
  avgSalary: string;
  jobOpenings: string;
}

export default function ProgramsInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('featured');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isAssessmentOpen, setIsAssessmentOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    categories: [],
    levels: [],
    duration: [],
    priceRange: []
  });
  const [assessmentResults, setAssessmentResults] = useState<any>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const mockPrograms: Program[] = [
  {
    id: '1',
    title: 'Full-Stack Web Development',
    description: 'ransform into a professional web developer through project-based learning and expert mentorship',
    image: "/assets/images/full-stack-hero.png",
    alt: 'Modern laptop displaying colorful code editor with React and Node.js development environment on wooden desk',
    duration: '6 months',
    level: 'Beginner',
    skillsCount: 8,
    projectsCount: 12,
    studentsEnrolled: 12450,
    rating: 4.8,
    category: 'Development',
    price: '',
    featured: true,
    url: '/java-full-stack-with-gen-ai',
    active: true
  },
  {
    id: '2',
    title: 'Gen AI Foundation for Beginners',
    description: 'Transform into an AI-ready professional through hands-on projects and expert guidance in generative AI technologies.',
    image: "/assets/images/gen-ai-hero.png",
    alt: 'Data visualization dashboard showing colorful charts, graphs and analytics on multiple computer screens',
    duration: '6 months',
    level: 'Beginner',
    skillsCount: 10,
    projectsCount: 5,
    studentsEnrolled: 9820,
    rating: 4.9,
    category: 'Data Science',
    price: '',
    featured: true,
    url: '/beginner-gen-ai',
    active: true
  },
  {
    id: '3',
    title: 'Mobile App Development with React Native',
    description: 'Build cross-platform mobile apps for iOS and Android using React Native and modern JavaScript.',
    image: "https://images.unsplash.com/photo-1576153193268-4f7dcb06ee99",
    alt: 'Smartphone displaying mobile app interface with colorful UI elements on designer workspace with sketches',
    duration: '5 months',
    level: 'Intermediate',
    skillsCount: 12,
    projectsCount: 6,
    studentsEnrolled: 7650,
    rating: 4.7,
    category: 'Mobile Development',
    price: '',
    featured: false,
    url: '/java-full-stack-with-gen-ai',
    active: false
  },
  {
    id: '4',
    title: 'Cloud Architecture & DevOps Engineering',
    description: 'Master AWS, Docker, Kubernetes, and CI/CD pipelines. Learn to build scalable cloud infrastructure.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1470b2657-1764669651426.png",
    alt: 'Abstract visualization of cloud computing network with glowing blue connections and data nodes',
    duration: '7 months',
    level: 'Advanced',
    skillsCount: 20,
    projectsCount: 12,
    studentsEnrolled: 5430,
    rating: 4.9,
    category: 'Cloud Computing',
    price: '',
    featured: false,
    url: '/java-full-stack-with-gen-ai',
    active: false
  },
  {
    id: '5',
    title: 'Cybersecurity Fundamentals',
    description: 'Learn ethical hacking, network security, and threat detection. Prepare for industry certifications.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18d1b5f69-1764669605749.png",
    alt: 'Digital security concept with glowing padlock icon and binary code on dark blue background',
    duration: '6 months',
    level: 'Beginner',
    skillsCount: 14,
    projectsCount: 7,
    studentsEnrolled: 6890,
    rating: 4.6,
    category: 'Cybersecurity',
    price: '',
    featured: false,
    url: '/java-full-stack-with-gen-ai',
    active: false
  },
  {
    id: '6',
    title: 'AI & Deep Learning Specialization',
    description: 'Dive deep into neural networks, computer vision, and NLP. Build AI-powered applications.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_113c8f6f0-1764648553585.png",
    alt: 'Futuristic AI visualization with neural network patterns and glowing blue artificial intelligence nodes',
    duration: '10 months',
    level: 'Advanced',
    skillsCount: 22,
    projectsCount: 15,
    studentsEnrolled: 4320,
    rating: 4.9,
    category: 'AI & Machine Learning',
    price: '',
    featured: false,
    url: '/java-full-stack-with-gen-ai',
    active: false
  }];


  const careerPaths: CareerPath[] = [
  {
    title: 'Full Stack Developer',
    description: 'Build complete web applications from frontend to backend with modern technologies.',
    icon: 'CodeBracketIcon',
    programs: 8,
    avgSalary: '₹6 LPA',
    jobOpenings: '8000+'
  },
  {
    title: 'Data Scientist',
    description: 'Analyze complex data and build predictive models to drive business decisions.',
    icon: 'ChartBarIcon',
    programs: 6,
    avgSalary: '₹6 LPA',
    jobOpenings: '7000+'
  },
  {
    title: 'Mobile Developer',
    description: 'Create engaging mobile experiences for iOS and Android platforms.',
    icon: 'DevicePhoneMobileIcon',
    programs: 5,
    avgSalary: '₹4 LPA',
    jobOpenings: '500+'
  },
  {
    title: 'Cloud Engineer',
    description: 'Design and implement scalable cloud infrastructure and DevOps solutions.',
    icon: 'CloudIcon',
    programs: 7,
    avgSalary: '₹9 LPA',
    jobOpenings: '5000+'
  }];


  const filterPrograms = (programs: Program[]) => {
    let filtered = [...programs];

    if (isHydrated && searchQuery) {
      filtered = filtered.filter(
        (program) =>
        program.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        program.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (isHydrated && filters.categories.length > 0) {
      filtered = filtered.filter((program) => filters.categories.includes(program.category));
    }

    if (isHydrated && filters.levels.length > 0) {
      filtered = filtered.filter((program) => filters.levels.includes(program.level));
    }

    if (isHydrated && sortBy === 'rating') {
      filtered.sort((a, b) => b.rating - a.rating);
    } else if (isHydrated && sortBy === 'students') {
      filtered.sort((a, b) => b.studentsEnrolled - a.studentsEnrolled);
    } else if (isHydrated && sortBy === 'featured') {
      filtered.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    }

    return filtered;
  };

  const filteredPrograms = filterPrograms(mockPrograms);

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary/5 via-accent/5 to-sky-light/5 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-headline font-bold text-foreground mb-6">
              Discover Your Perfect
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent mt-2">
                Learning Path
              </span>
            </h1>
            <p className="text-lg md:text-xl font-body text-muted-foreground mb-8">
              Explore industry-aligned programs designed to transform your career. From beginner to advanced, find the perfect match for your goals.
            </p>
            <button
              onClick={() => setIsAssessmentOpen(true)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-primary to-accent text-primary-foreground rounded-lg font-headline font-semibold text-lg shadow-sky-md hover:shadow-sky-lg transition-all duration-300 hover:scale-105">

              <Icon name="AcademicCapIcon" size={24} variant="outline" />
              Take Skill Assessment
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
            { label: 'Active Programs', value: '3+', icon: 'AcademicCapIcon' },
            { label: 'Students Enrolled', value: '850+', icon: 'UsersIcon' },
            { label: 'Career Transitions', value: '160+', icon: 'BriefcaseIcon' }].
            map((stat, index) =>
            <div key={index} className="bg-card rounded-xl shadow-sky-sm p-6 text-center">
                <Icon name={stat.icon as any} size={32} variant="outline" className="text-primary mx-auto mb-3" />
                <p className="text-2xl font-headline font-bold text-foreground mb-1">{stat.value}</p>
                <p className="text-sm font-body text-muted-foreground">{stat.label}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Assessment Results */}
      {isHydrated && assessmentResults &&
      <section className="py-8 bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-card rounded-xl shadow-sky-md p-6 md:p-8">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-success/10 rounded-lg">
                  <Icon name="CheckCircleIcon" size={32} variant="solid" className="text-success" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                    Assessment Complete! Your Level: {assessmentResults.level}
                  </h3>
                  <p className="text-sm font-body text-muted-foreground mb-4">
                    Based on your responses, we recommend these programs:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {assessmentResults.recommendedPrograms.map((program: string, index: number) =>
                  <span
                    key={index}
                    className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-body font-medium">

                        {program}
                      </span>
                  )}
                  </div>
                </div>
                <button
                onClick={() => setAssessmentResults(null)}
                className="p-2 hover:bg-muted rounded-lg transition-colors duration-200"
                aria-label="Close results">

                  <Icon name="XMarkIcon" size={20} variant="outline" />
                </button>
              </div>
            </div>
          </div>
        </section>
      }

      {/* Career Paths */}
      <section className="py-16 bg-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-4">
              Explore Career Paths
            </h2>
            <p className="text-lg font-body text-muted-foreground max-w-2xl mx-auto">
              Choose your destination and we'll guide you with the right programs and skills
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            {careerPaths.map((path, index) =>
            <CareerPathCard key={index} path={path} />
            )}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search and Controls */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
              <div className="flex-1 w-full lg:max-w-xl">
                <div className="relative">
                  <Icon
                    name="MagnifyingGlassIcon"
                    size={20}
                    variant="outline"
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />

                  <input
                    type="text"
                    placeholder="Search programs..."
                    value={isHydrated ? searchQuery : ''}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-card border border-border rounded-lg font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200" />

                </div>
              </div>

              <div className="flex items-center gap-3 w-full lg:w-auto">
                <button
                  onClick={() => setIsFilterOpen(true)}
                  className="lg:hidden flex items-center gap-2 px-4 py-3 bg-card border border-border rounded-lg font-body font-medium text-foreground hover:bg-muted transition-colors duration-200">

                  <Icon name="FunnelIcon" size={20} variant="outline" />
                  Filters
                </button>

                <select
                  value={isHydrated ? sortBy : 'featured'}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="flex-1 lg:flex-none px-4 py-3 bg-card border border-border rounded-lg font-body text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200">

                  <option value="featured">Featured</option>
                  <option value="rating">Highest Rated</option>
                  <option value="students">Most Popular</option>
                </select>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm font-body text-muted-foreground">
                {isHydrated ? filteredPrograms.length : mockPrograms.length} programs found
              </p>
            </div>
          </div>

          {/* Programs Grid */}
          <div className="flex gap-8">
            <FilterSidebar
              onFilterChange={setFilters}
              isMobileOpen={isFilterOpen}
              onMobileClose={() => setIsFilterOpen(false)} />


            <div className="flex-1">
              {filteredPrograms.length > 0 ?
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredPrograms.map((program) =>
                <ProgramCard key={program.id} program={program} />
                )}
                </div> :

              <div className="text-center py-16">
                  <Icon name="FolderOpenIcon" size={64} variant="outline" className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-headline font-bold text-foreground mb-2">No programs found</h3>
                  <p className="text-sm font-body text-muted-foreground">
                    Try adjusting your filters or search query
                  </p>
                </div>
              }
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-primary/5 via-accent/5 to-sky-light/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-4">
            Not Sure Where to Start?
          </h2>
          <p className="text-lg font-body text-muted-foreground mb-8">
            Take our skill assessment to get personalized program recommendations based on your goals and experience
          </p>
          <button
            onClick={() => setIsAssessmentOpen(true)}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-primary to-accent text-primary-foreground rounded-lg font-headline font-semibold text-lg shadow-sky-md hover:shadow-sky-lg transition-all duration-300 hover:scale-105">

            <Icon name="SparklesIcon" size={24} variant="outline" />
            Get Personalized Recommendations
          </button>
        </div>
      </section>

      <SkillAssessmentModal
        isOpen={isAssessmentOpen}
        onClose={() => setIsAssessmentOpen(false)}
        onComplete={(results) => {
          setAssessmentResults(results);
          setIsAssessmentOpen(false);
        }} />

    </div>);

}